import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Local copy of a segment the user edits, kept in step with the parent's `value`. Only user edits reach
 * `onChange`; echoing a value the parent just set would make parent and child trade states forever.
 */
export function useSyncedSegment<T extends object>(
	value: T | undefined,
	onChange: (segment: T) => void,
	keys: readonly (keyof T)[],
	empty: T,
) {
	const [segment, setSegment] = useState<T>(() => pickSegment(value, keys, empty));
	const userEditedRef = useRef(false);

	const segmentChanged = useCallback((fieldValue: unknown, name: keyof T) => {
		userEditedRef.current = true;
		setSegment((prev) => ({ ...prev, [name]: fieldValue }));
	}, []);

	useEffect(() => {
		if (!value) return;
		userEditedRef.current = false;
		// oxlint-disable-next-line react/set-state-in-effect -- render-phase updates were lost under StrictMode in tlp-cloud
		setSegment((prev) => {
			const next = pickSegment(value, keys, empty);
			return isSameSegment(prev, next, keys) ? prev : next;
		});
	}, [value, keys, empty]);

	useEffect(() => {
		if (!userEditedRef.current) return;
		userEditedRef.current = false;
		onChange(segment);
	}, [segment, onChange]);

	return [segment, segmentChanged] as const;
}

function pickSegment<T extends object>(value: T | undefined, keys: readonly (keyof T)[], empty: T): T {
	const picked = { ...empty };
	if (!value) return picked;

	for (const key of keys) {
		if (value[key] !== undefined) picked[key] = value[key];
	}

	return picked;
}

function isSameSegment<T extends object>(a: T, b: T, keys: readonly (keyof T)[]) {
	return keys.every((key) => {
		const left = a[key];
		const right = b[key];

		if (Array.isArray(left) && Array.isArray(right))
			return left.length === right.length && left.every((item, i) => item === right[i]);

		return left === right;
	});
}
